import { z } from 'zod';

export const selectedCustomer = (search: string) => {
  const values = new URLSearchParams(search).getAll('customer');
  if (values.length === 0) return { customerUUID: null, error: null };
  const parsed = values.length === 1 ? z.uuid().safeParse(values[0]) : null;
  return parsed?.success
    ? { customerUUID: parsed.data.toLowerCase(), error: null }
    : { customerUUID: null, error: 'invalid-customer' as const };
};

export type SupportSessionConfig = Readonly<{
  applicationOrigin: string;
  authOrigin: string;
  entryOrigins: readonly string[];
}>;

type SupportSessionRedirectUrlOptions = Readonly<{
  /** True when the application has identified a support session that needs employee authentication. */
  shouldAuthenticate: boolean;
  /** True when the current application origin already has an authenticated employee session. */
  authenticatedInternalUser?: boolean;
  config: SupportSessionConfig;
  /** Current browser URL whose route, query, and fragment must be preserved. */
  currentUrl: string;
}>;

export const isSupportApplicationUrl = ({
  currentUrl,
  config,
}: Pick<SupportSessionRedirectUrlOptions, 'currentUrl' | 'config'>): boolean =>
  new URL(currentUrl).origin === config.applicationOrigin;

export const getSupportSessionRedirectUrl = ({
  currentUrl,
  config,
  shouldAuthenticate,
  authenticatedInternalUser = false,
}: SupportSessionRedirectUrlOptions): string | null => {
  const current = new URL(currentUrl);
  const isConfiguredOrigin =
    current.origin === config.applicationOrigin || config.entryOrigins.includes(current.origin);
  if (!isConfiguredOrigin) {
    return null;
  }

  if (current.origin === config.applicationOrigin && authenticatedInternalUser) {
    return null;
  }
  if (!shouldAuthenticate) {
    return null;
  }

  const destination = new URL(config.applicationOrigin);
  destination.pathname = current.pathname;
  destination.search = current.search;
  destination.searchParams.delete('human_token');
  destination.hash = current.hash;

  const authEntry = new URL('/oauth2/start', config.authOrigin);
  authEntry.searchParams.set('rd', destination.toString());
  return authEntry.toString();
};
